import { z } from 'zod';

/**
 * `data/project.json`.
 *
 * Every field the marketing shell renders comes from here. Components never
 * hardcode a project fact — a wrong phone number must be fixable in one file
 * by someone who does not write TypeScript.
 *
 * `advertisingLicence` and `wafiRegistration` are nullable **on purpose**: they
 * are unanswered (§15) and a public real-estate marketing page in Saudi Arabia
 * is expected to display them. Null renders a visible operator warning in dev
 * and omits the line in production rather than inventing a number.
 */
export const ContactSchema = z.strictObject({
  phone: z
    .string()
    .regex(/^[0-9]{7,12}$/, 'digits only, no spaces or country prefix'),
  social: z.string().min(1),
  whatsapp: z.string().regex(/^[0-9]{9,15}$/).nullish(),
});

export const SeoSchema = z.strictObject({
  titleAr: z.string().min(1),
  descriptionAr: z.string().min(1),
  titleEn: z.string().min(1),
  descriptionEn: z.string().min(1),
  ogImage: z.string().min(1),
});

export const ProjectSchema = z.strictObject({
  id: z.string().min(1),
  nameAr: z.string().min(1),
  developerAr: z.string().min(1),
  marketerAr: z.string().min(1),
  /**
   * Demo mode: catalogue facts (prices, layout scale, statuses) are mocked
   * for capability proof, and the interface shows a persistent
   * «نسخة تجريبية — بيانات افتراضية» badge while this is true. Licence
   * numbers are NEVER mocked regardless of this flag — they stay null until
   * the real ones exist.
   */
  demoData: z.boolean().optional(),
  cityAr: z.string().min(1),
  districtAr: z.string().min(1),
  highlightsAr: z.array(z.string().min(1)).min(1),
  locationNotesAr: z.array(z.string().min(1)),
  contact: ContactSchema,
  /** REGA advertising licence. Unanswered — §15. */
  advertisingLicence: z.string().min(1).nullable(),
  /** Wafi off-plan sales registration. Unanswered — §15. */
  wafiRegistration: z.string().min(1).nullable(),
  seo: SeoSchema,
});

export type Contact = z.infer<typeof ContactSchema>;
export type Seo = z.infer<typeof SeoSchema>;
export type Project = z.infer<typeof ProjectSchema>;

/** Compliance lines that must appear before a public launch (§15). */
export function missingComplianceFields(project: Project): string[] {
  const missing: string[] = [];
  if (project.advertisingLicence === null) missing.push('advertisingLicence');
  if (project.wafiRegistration === null) missing.push('wafiRegistration');
  return missing;
}
